import { useCallback, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { EducateNavHeader } from '@/components/brand/EducateNavHeader';
import { ErrorScreen, LoadingScreen, ProgressBar } from '@/components/StatusScreens';
import { UnitRenderer } from '@/components/units/UnitRenderer';
import { isBranching, isNarrated, EDUCATE_TYPE_LABEL } from '@/utils/units';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { EducateInteraction } from '@/types/educate';

export function GenericModulePage({ module }: { module: EducateModule }) {
  const {
    storyline,
    units,
    currentUnit,
    currentIndex,
    percentComplete,
    isFirstUnit,
    advance,
    goBack
  } = module;

  const pending = useRef<EducateInteraction[]>([]);
  const [canAdvance, setCanAdvance] = useState(true);

  const onInteractions = useCallback((interactions: EducateInteraction[]) => {
    pending.current = interactions;
  }, []);
  const onGateChange = useCallback((open: boolean) => setCanAdvance(open), []);
  const onBranch = useCallback(
    (nextUnitId: string | null) => advance(pending.current, nextUnitId),
    [advance]
  );
  const onNext = () => advance(pending.current);

  const branching = storyline ? isBranching(storyline.educateType) : false;

  return (
    <EducateNavHeader
      eyebrow={storyline ? EDUCATE_TYPE_LABEL[storyline.educateType] : 'Educate'}
      title={storyline?.uiContent.title ?? '4E Educate'}
      showLogo={storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true}
    >
      <main className="w-full px-[18%] py-12">
        {units.length === 0 ? (
          <ErrorScreen
            message={`This module's format (${storyline?.educateType}) cannot be displayed yet.`}
          />
        ) : !currentUnit ? (
          <LoadingScreen label="Finding your place…" />
        ) : (
          <div className="space-y-8" data-testid="module-screen">
            <ProgressBar
              percent={percentComplete}
              label={`${currentUnit.label} — ${currentIndex + 1} of ${units.length}`}
            />

            <div>
              <h2 className="text-2xl font-semibold">{currentUnit.title}</h2>
            </div>

            <UnitRenderer
              key={currentUnit.id}
              unit={currentUnit}
              narrationEnabled={storyline ? isNarrated(storyline.educateType) : false}
              onInteractions={onInteractions}
              onBranch={onBranch}
              onGateChange={onGateChange}
            />

            {!branching && (
              <div className="flex items-center justify-between border-t border-slate-200 pt-6 dark:border-slate-800">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={isFirstUnit}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium disabled:opacity-40 dark:border-slate-700"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                <button
                  type="button"
                  onClick={onNext}
                  disabled={!canAdvance}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
                >
                  {currentIndex === units.length - 1 ? 'Finish' : 'Next'}
                  <ArrowRight size={16} />
                </button>
              </div>
            )}

            {!canAdvance && !branching && (
              <p className="text-xs text-slate-500">
                Answer the knowledge check above to continue.
              </p>
            )}
          </div>
        )}
      </main>
    </EducateNavHeader>
  );
}
